import { LiveSample } from '@/components/tools/LiveSample';
import { useEffect, useState, type PointerEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, AudioWaveform, Captions, Check, Clapperboard, Film, Repeat, Scissors, type LucideIcon } from 'lucide-react';
import quickTools from '@/components/quicktools/tools.json';
import { getQuickToolsConfig, money, type QuickToolKey } from '@/lib/quicktools-api';
import { hasFinePointer } from '@/lib/motion';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import '@/components/landing/immersive/hero.css';

// The quick tools as the site's own tiles (owner, 2026-10-16: "presented
// correctly as is every other tool we have"): a real sample of what each one
// makes, the name, one line, the price per file in amber and "free preview
// first" in teal, and a labelled button. Router links: the tools are SPA
// pages inside the site's frame now.

const ICONS: Record<string, LucideIcon> = {
  captions: Captions,
  clapperboard: Clapperboard,
  scissors: Scissors,
  repeat: Repeat,
  waveform: AudioWaveform,
  film: Film,
};

// Shown until /api/quicktools/config answers (the server's defaults).
export const DEFAULT_CENTS: Record<QuickToolKey, number> = { captions: 200, faceless: 900, hooks: 500, canvas: 400, vows: 300, tapes: 1900 };
// The price a tile shows: the cheapest way in. The Living Cover's is just a moving picture, $3 (owner 2026-10-23).
const FROM_CENTS: Record<QuickToolKey, number> = { ...DEFAULT_CENTS, canvas: 300 };

const TILE =
  'hero-hairline hero-tile relative overflow-hidden rounded-[1.35rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.018))] shadow-[0_24px_48px_-28px_rgba(0,0,0,0.8)]';

export function useQuickToolPrices(): Record<QuickToolKey, number> {
  const [prices, setPrices] = useState<Record<QuickToolKey, number>>(FROM_CENTS);
  useEffect(() => {
    getQuickToolsConfig()
      .then((c) => setPrices((prev) => {
        const next = { ...prev };
        (Object.keys(c) as QuickToolKey[]).forEach((key) => {
          if (c[key]?.price_cents) next[key] = Math.min(c[key].price_cents, c[key].picture_price_cents ?? c[key].price_cents);
        });
        return next;
      }))
      .catch(() => undefined);
  }, []);
  return prices;
}

export function QuickToolCards({ exclude, audience }: { exclude?: QuickToolKey; audience?: 'creator' | 'family' }) {
  const { t } = useTranslation();
  const prices = useQuickToolPrices();
  const [fine, setFine] = useState(false);
  useEffect(() => setFine(hasFinePointer()), []);
  const spotlight = (e: PointerEvent<HTMLElement>) => {
    if (!fine) return;
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  const keys = (quickTools.order as QuickToolKey[]).filter(
    (key) => key !== exclude && (!audience || quickTools.tools[key].audience === audience),
  );

  return (
    <ul className={cn('grid gap-3 sm:grid-cols-2 lg:gap-4', keys.length >= 5 ? 'lg:grid-cols-6' : keys.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4')} data-testid="quick-tool-cards">
      {keys.map((key, i) => {
        const tool = quickTools.tools[key];
        const Icon = ICONS[tool.icon] || Captions;
        // Five across lg: three on top, two wider below; on tablets the fifth spans the row.
        const span = keys.length >= 5 ? (i < 3 ? 'lg:col-span-2' : 'lg:col-span-3') + (i === 4 ? ' sm:col-span-2' : '') : '';
        return (
          <li key={key} className={span}>
            <Link
              to={tool.path}
              data-testid={`quick-tool-${key}`}
              onPointerMove={spotlight}
              onClick={() => trackEvent('quicktool_card_clicked', { tool: key })}
              className={`${TILE} group flex h-full flex-col transition-transform duration-500 ease-expo-out [@media(hover:hover)_and_(pointer:fine)]:hover:-translate-y-1`}
            >
              <span className="relative z-10 block aspect-[16/9] overflow-hidden rounded-t-[1.35rem] bg-black">
                {/* The result itself, moving (owner 2026-10-16). */}
                <LiveSample
                  poster={tool.sample.poster}
                  src={tool.sample.type === 'video' ? tool.sample.src : undefined}
                  upright={tool.sample.aspect === '9/16'}
                  gallery={'gallery' in tool ? (tool as { gallery?: { src: string; poster: string }[] }).gallery : undefined}
                />
                <span aria-hidden className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" />
                <span className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-xl bg-black/55 text-primary ring-1 ring-inset ring-primary/30 backdrop-blur-sm">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="absolute right-3 top-3 flex items-baseline gap-1 rounded-full bg-black/60 px-3 py-1 ring-1 ring-inset ring-primary/35 backdrop-blur-sm">
                  <span className="font-display text-base font-bold tabular-nums text-primary">{money(prices[key])}</span>
                  <span className="text-[12px] font-medium text-primary/80">{t('qt.perFile')}</span>
                </span>
              </span>
              <span className="relative z-10 flex flex-1 flex-col p-5">
                <span className="font-display text-[1.15rem] font-semibold leading-snug text-foreground">{t(`qt.tools.${key}.name`)}</span>
                <span className="mt-2 flex-1 text-pretty text-[15px] leading-relaxed text-white/60">{t(`qt.tools.${key}.blurb`)}</span>
                <span className="mt-5 flex flex-wrap items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-accent">
                    <Check className="h-4 w-4" aria-hidden="true" />
                    {t('qt.chips.freePreview')}
                  </span>
                  <span className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-5 text-[0.95rem] font-semibold text-foreground transition-colors duration-300 group-hover:border-primary/50 group-hover:bg-primary/10">
                    {t(`qt.tools.${key}.open`)}
                    <ArrowUpRight aria-hidden="true" className="h-4 w-4 text-primary transition-transform duration-300 ease-expo-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The five quick tools as one compact row (home's first screen, owner
 * 2026-10-16: "the five quick tools get a row there too"): a real sample, the
 * name and the price per file on each. Swipes sideways on a phone.
 */
export function QuickToolRow({ className }: { className?: string }) {
  const { t } = useTranslation();
  const prices = useQuickToolPrices();
  return (
    <ul
      data-testid="quick-tool-row"
      className={cn('-mx-4 flex snap-x gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:grid sm:grid-cols-5 sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden', className)}
    >
      {(quickTools.order as QuickToolKey[]).map((key) => {
        const tool = quickTools.tools[key];
        return (
          <li key={key} className="w-[9.5rem] shrink-0 snap-start sm:w-auto">
            <Link
              to={tool.path}
              data-testid={`quick-row-${key}`}
              onClick={() => trackEvent('quicktool_card_clicked', { tool: key, from: 'home_row' })}
              className="group flex h-full items-center gap-2.5 rounded-2xl border border-white/10 bg-white/[0.03] p-2 transition-colors hover:border-primary/45"
            >
              <img src={tool.sample.poster} alt="" width={44} height={44} loading="lazy" decoding="async" className="h-11 w-11 shrink-0 rounded-xl object-cover" />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold leading-tight text-foreground">{t(`qt.tools.${key}.name`)}</span>
                <span className="mt-0.5 block text-[12px] font-semibold tabular-nums text-primary">
                  {money(prices[key])} <span className="font-medium text-primary/75">{t('qt.perFile')}</span>
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
